import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';

/**
 * @typedef {Object} ApiResult
 * @property {Record<string, any> | null} data The result data
 * @property {Error | null} error The result error
 * @property {number} status The result status code
 */

/**
 * The success response handler
 * @param {AxiosResponse} response 
 * @returns {ApiResult} The result data
 */
function onSuccess(response) {
  return {
    data: response.data,
    error: null,
    status: response.status,
  };
}

/**
 * The error response handler
 * @param {AxiosResponse} response 
 * @returns {ApiResult} The result data
 */
function onError(error) {
  return {
    data: null,
    error,
    status: 0,
  };
}

/**
 * Executes an GET request
 * @param {string} url URL or path to be requested
 * @param {AxiosRequestConfig} [config] Additional configuration
 * 
 * @returns {Promise<ApiResult>} An object containing the data, error and status values
 * 
 * @example
 * 
 * ```js
 * // Asynchronous usage example
 * $get('/api/user', {
 *   params: {
 *     id: 2,
 *   },
 * })
 *   .then(({ data, error, status }) => {
 *     console.log(data);
 *     console.log(error);
 *     console.log(status);
 *   });
 * ```
 * 
 * @example
 * 
 * ```js
 * // Synchronous usage example
 * const { data, error, status } = await $get('/api/user', {
 *   params: {
 *     id: 2,
 *   },
 * });
 * 
 * console.log(data);
 * console.log(error);
 * console.log(status);
 * ```
 */
async function $get(url, config) {
  try {
    const response = await axios.get(url, config);

    return onSuccess(response);
  } catch (error) {
    return onError(error);
  }
};

/**
 * Executes an POST request
 * @param {string} url URL or path to be requested
 * @param {AxiosRequestConfig} [config] Additional configuration
 * 
 * @returns {Promise<ApiResult>} An object containing the data, error and status values
 * 
 * @example
 * 
 * ```js
 * // Asynchronous usage example
 * $post('/api/user', {
 *   name: 'John Doe',
 * })
 *   .then(({ data, error, status }) => {
 *     console.log(data);
 *     console.log(error);
 *     console.log(status);
 *   });
 * ```
 * 
 * @example
 * 
 * ```js
 * // Synchronous usage example
 * const { data, error, status } = await $post('/api/user', {
 *   name: 'John Doe',
 * });
 * 
 * console.log(data);
 * console.log(error);
 * console.log(status);
 * ```
 */
async function $post(url, config) {
  try {
    const response = await axios.post(url, config);

    return onSuccess(response);
  } catch (error) {
    return onError(error);
  }
};

/**
 * Executes an PUT request
 * @param {string} url URL or path to be requested
 * @param {AxiosRequestConfig} [config] Additional configuration
 * 
 * @returns {Promise<ApiResult>} An object containing the data, error and status values
 * 
 * @example
 * 
 * ```js
 * // Asynchronous usage example
 * $put('/api/user', {
 *   name: 'Foo Bar',
 * })
 *   .then(({ data, error, status }) => {
 *     console.log(data);
 *     console.log(error);
 *     console.log(status);
 *   });
 * ```
 * 
 * @example
 * 
 * ```js
 * // Synchronous usage example
 * const { data, error, status } = await $put('/api/user', {
 *   name: 'Foo Bar',
 * });
 * 
 * console.log(data);
 * console.log(error);
 * console.log(status);
 * ```
 */
async function $put(url, config) {
  try {
    const response = await axios.put(url, config);

    return onSuccess(response);
  } catch (error) {
    return onError(error);
  }
};

/**
 * Executes an PATCH request
 * @param {string} url URL or path to be requested
 * @param {AxiosRequestConfig} [config] Additional configuration
 * 
 * @returns {Promise<ApiResult>} An object containing the data, error and status values
 * 
 * @example
 * 
 * ```js
 * // Asynchronous usage example
 * $patch('/api/user', {
 *   name: 'Foo Baz',
 * })
 *   .then(({ data, error, status }) => {
 *     console.log(data);
 *     console.log(error);
 *     console.log(status);
 *   });
 * ```
 * 
 * @example
 * 
 * ```js
 * // Synchronous usage example
 * const { data, error, status } = await $patch('/api/user', {
 *   name: 'Foo Baz',
 * });
 * 
 * console.log(data);
 * console.log(error);
 * console.log(status);
 * ```
 */
async function $patch(url, config) {
  try {
    const response = await axios.patch(url, config);

    return onSuccess(response);
  } catch (error) {
    return onError(error);
  }
};

/**
 * Executes an DELETE request
 * @param {string} url URL or path to be requested
 * @param {AxiosRequestConfig} [config] Additional configuration
 * 
 * @returns {Promise<ApiResult>} An object containing the data, error and status values
 * 
 * @example
 * 
 * ```js
 * // Asynchronous usage example
 * $delete('/api/user/2')
 *   .then(({ data, error, status }) => {
 *     console.log(data);
 *     console.log(error);
 *     console.log(status);
 *   });
 * ```
 * 
 * @example
 * 
 * ```js
 * // Synchronous usage example
 * const { data, error, status } = await $delete('/api/user/2');
 * 
 * console.log(data);
 * console.log(error);
 * console.log(status);
 * ```
 */
async function $delete(url, config) {
  try {
    const response = await axios.delete(url, config);

    return onSuccess(response);
  } catch (error) {
    return onError(error);
  }
};

export {
  $get,
  $post,
  $put,
  $patch,
  $delete,
};